import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '@applications/sha-services';
import { datadogRum } from '@datadog/browser-rum';
import { Store } from '@ngrx/store';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { environment } from '@quorum/environments';
import { MediaStateService } from '@quorum/sha-media';
import { combineLatest, EMPTY, forkJoin, interval, Observable, Subject, Subscription, throwError, timer } from 'rxjs';
import { catchError, distinctUntilKeyChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { MediaUploadOptionsDialogComponent } from './media-upload-options-dialog/media-upload-options-dialog.component';
import { MediaUploadSnackbarComponent } from './media-upload-snackbar/media-upload-snackbar.component';

export interface MediaRecorderConfig {
  mimeType: string;
  videoBitsPerSecond: number;
  audioBitsPerSecond: number;
  timeslice: number;
  quality: 'conservative' | 'balanced' | 'aggressive';
}

export interface CameraAnalysis {
  resolution: { width: number; height: number };
  pixels: number;
  frameRate: number;
  aspectRatio: number;
  facingMode: string;
  deviceCapabilities: MediaTrackCapabilities;
}

interface MultipartInitializeResponse {
  uploadId: string;
}

@Injectable({
  providedIn: 'root',
})
export class MediaUploadService {
  private readonly chunkSize = 5 * 1024 * 1024; // 5MB chunk size

  private readonly STALL_TIMEOUT_MS = 15_000;
  private readonly MAX_RETRY_ATTEMPTS = 5;
  private readonly BASE_BACKOFF_MS = 2_000;
  private readonly STALL_CHECK_INTERVAL_MS = 2000;

  mediaUploadTimer$: Subscription;
  mediaNotifier = new Subject();

  debugLogs: string[] = [];
  maxDebugLogs = 200;
  logFile: string[] = [];

  constructor(
    private dialog: MatDialog,
    private mediaStateService: MediaStateService,
    private snackbarService: SnackbarService,
    private http: HttpClient,
    private authenticationStateService: AuthenticationStateService,
    private apiService: ApiService,
    private store: Store
  ) {}

  uploadFile(
    caption: string,
    customerFacing: boolean,
    fileName: string,
    fileType: string,
    duration: number = null,
    fileSize: number,
    file: File,
    params: any
  ) {
    const dialogRef = this.dialog.open(MediaUploadOptionsDialogComponent, {
      width: '80%',
      maxWidth: '350px',
      disableClose: true,
      data: {
        caption: caption,
        customerFacing: customerFacing,
      },
    });

    dialogRef.componentInstance.uploadClicked
      .pipe(
        take(1),
        switchMap((result): Observable<any> => {
          if (!result) {
            return EMPTY;
          }

          this.mediaStateService.uploadMedia({
            fileName: fileName.split('.')[0],
            fileType: fileType.split('/')[1],
            fileStatus: 'Uploading',
            fileNameToDisplay: file.name.substring(0, file.name.lastIndexOf('.')),
          });
          this.snackbarService.showCustomSnackbar(MediaUploadSnackbarComponent, {
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['media-snackbar'],
          });

          let requestheaders = new HttpHeaders();
          requestheaders = requestheaders.append('x-file-name', fileName);
          requestheaders = requestheaders.append('x-file-type', fileType);

          const timeStamp = new Date().toISOString();
          const queryParams = {
            ...params,
            dateAdded: timeStamp,
            caption: result.caption,
            galleryDescription: params.galleryDescription.substring(0, 75),
            customerFacing: result.customerFacing,
          };

          if (Number.isFinite(duration) && duration > 0) {
            queryParams.duration = duration;
            queryParams.durationFormatted = new Date(Math.round(duration) * 1000).toISOString().substring(14, 19);
          }

          Object.keys(queryParams).forEach((p) => {
            queryParams[p] = encodeURIComponent(queryParams[p]);
          });

          const t0 = performance.now();
          if (fileSize > 20 * 1024 * 1024) {
            return this.multiPartUpload(requestheaders, queryParams, fileName, fileType, t0, file, params);
          } else {
            return this.simpleFileUpload(requestheaders, queryParams, fileName, fileType, t0, file, params);
          }
        })
      )
      .subscribe({
        error: () => {
          this.snackbarService.showSnackbar(`Upload failed`, 'Close', { duration: 5000 });
          this.mediaStateService.clearUploadingMediaState();
        },
      });
  }

  uploadMediaRecorderFile(
    caption: string,
    customerFacing: boolean,
    fileName: string,
    fileType: string,
    duration: number = null,
    fileSize: number,
    file: File,
    params: any
  ) {
    this.mediaStateService.uploadMedia({
      fileName: fileName.split('.')[0],
      fileType: fileType.split('/')[1],
      fileStatus: 'Uploading',
      fileNameToDisplay: file.name.substring(0, file.name.lastIndexOf('.')),
    });

    this.dispatchMediaUploading(true);

    this.snackbarService.showCustomSnackbar(MediaUploadSnackbarComponent, {
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['media-snackbar'],
    });

    let requestheaders = new HttpHeaders();
    requestheaders = requestheaders.append('x-file-name', fileName);
    requestheaders = requestheaders.append('x-file-type', fileType);

    const timeStamp = new Date().toISOString();
    const queryParams = {
      ...params,
      dateAdded: timeStamp,
      caption: caption,
      galleryDescription: params.galleryDescription.substring(0, 75),
      customerFacing: customerFacing,
    };

    if (Number.isFinite(duration) && duration > 0) {
      queryParams.duration = duration;
      queryParams.durationFormatted = new Date(Math.round(duration) * 1000).toISOString().substring(14, 19);
    }

    Object.keys(queryParams).forEach((p) => {
      queryParams[p] = encodeURIComponent(queryParams[p]);
    });

    const t0 = performance.now();

    if (fileSize > 20 * 1024 * 1024) {
      return this.multiPartUpload(requestheaders, queryParams, fileName, fileType, t0, file, params).subscribe({
        error: () => {
          this.snackbarService.showSnackbar('Upload failed', 'Close', { duration: 5000 });
          this.mediaStateService.clearUploadingMediaState();
          this.dispatchMediaUploading(false);
        },
      });
    } else {
      return this.simpleFileUpload(requestheaders, queryParams, fileName, fileType, t0, file, params).subscribe({
        error: () => {
          this.snackbarService.showSnackbar('Upload failed', 'Close', { duration: 5000 });
          this.mediaStateService.clearUploadingMediaState();
          this.dispatchMediaUploading(false);
        },
      });
    }
  }

  convertStringCase(str: string) {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }

  generateId(length: number) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }

  private multiPartUpload(
    requestheaders: HttpHeaders,
    queryParams: any,
    fileName: string,
    fileType: string,
    t0: number,
    file: File,
    params: any
  ): Observable<any> {
    const url = new URL(`${environment.multiPartMediaUploadUrl}/initialize`);
    url.search = new URLSearchParams(queryParams).toString();

    return this.http.post<MultipartInitializeResponse>(url.toString(), { fileName, fileType }).pipe(
      switchMap((initializeResponse) => {
        const uploadId = initializeResponse.uploadId;
        const totalParts = Math.ceil(file.size / this.chunkSize);
        return this.getPreSignedUrls(fileName, fileType, uploadId, totalParts, requestheaders, queryParams).pipe(
          switchMap((presignedUrlsResponse) => {
            const presignedUrls = presignedUrlsResponse['signedUrls'];
            const chunks = this.getChunks(file, this.chunkSize);

            if (!Array.isArray(presignedUrls) || presignedUrls.length !== chunks.length) {
              return throwError(
                () =>
                  new Error('Multipart upload initialization failed: mismatch between presigned URLs and file chunks.')
              );
            }

            const allUrlsValid = presignedUrls.every((url: unknown) => {
              if (typeof url !== 'string') {
                return false;
              }
              const trimmed = url.trim();
              if (!trimmed) {
                return false;
              }
              try {
                // Validate URL format using the built-in URL parser
                // This will throw if the URL is not well-formed
                new URL(trimmed);
                return true;
              } catch {
                return false;
              }
            });
            if (!allUrlsValid) {
              return throwError(
                () => new Error('Multipart upload initialization failed: invalid presigned URL(s) provided.')
              );
            }
            const uploadObservables = (presignedUrls as string[]).map((url: string, index: number) =>
              this.uploadChunkWithRetry(url, chunks[index], fileType, index + 1)
            );
            return forkJoin(uploadObservables);
          }),
          switchMap((uploadedParts: { PartNumber: number; ETag: string }[]) => {
            return this.finalizeUpload(uploadId, uploadedParts, fileName, fileType).pipe(
              tap(() => {
                this.mediaStateService.mediaUploaded(this.buildMediaUploadedPayload(fileName, fileType));
              })
            );
          }),
          tap(() => this.finalizeMediaUpload(fileName, fileType, t0, params, queryParams.duration))
        );
      }),
      catchError((error) => {
        console.error('Initialize error', error);
        return throwError(() => new Error('Initialization failed'));
      })
    );
  }

  private getPreSignedUrls(
    fileName: string,
    fileType: string,
    uploadId: string,
    totalParts: number,
    requestheaders: HttpHeaders,
    queryParams: any
  ): Observable<any> {
    return this.http.get(
      `${environment.multiPartMediaUploadUrl}/getPreSignedUrls/${encodeURIComponent(fileName)}/${encodeURIComponent(
        fileType
      )}/${uploadId}/${totalParts}`,
      {
        headers: requestheaders,
        params: queryParams,
      }
    );
  }

  private getChunks(file: File, chunkSize: number) {
    const chunks = [];
    let currentPosition = 0;
    while (currentPosition < file.size) {
      const chunk = file.slice(currentPosition, currentPosition + chunkSize);
      chunks.push(chunk);
      currentPosition += chunkSize;
    }
    return chunks;
  }

  private finalizeUpload(
    uploadId: string,
    uploadedParts: { PartNumber: number; ETag: string }[],
    fileName: string,
    fileType: string
  ): Observable<any> {
    return this.http.post(`${environment.multiPartMediaUploadUrl}/finalize`, {
      uploadId,
      parts: uploadedParts,
      fileName,
      fileType,
    });
  }

  private finalizeMediaUpload(fileName: string, fileType: string, t0: number, params: any, videoDuration?: number) {
    const specificFileMediaName = this.extractFileName(fileName);
    this.monitorMediaUpload(specificFileMediaName, t0);

    this.mediaStateService.createUploadMediaPowerLaneAnalyticEvent(
      params,
      this.getFileTypeForAnalytics(fileType),
      videoDuration
    );
  }

  private extractFileName(fullFileName: string): string {
    return fullFileName.substring(fullFileName.indexOf('/') + 1, fullFileName.indexOf('.'));
  }

  /**
   * Monitors media upload progress.
   * Polls AWS every 2 seconds for status updates and displays snackbar notifications.
   *
   * @param specificFileMediaName - Unique identifier for the file being monitored
   * @param t0 - Timestamp when upload started (for metrics)
   */
  private monitorMediaUpload(specificFileMediaName: string, t0: number) {
    // Combines three streams:
    // 1. Polling interval to refresh media state
    // 2. File processing status with from uploaded file state
    // 3. Current list of uploading files for UI state
    this.mediaUploadTimer$ = combineLatest([
      this.createPollingStream(),
      this.createUploadedFileStream(specificFileMediaName),
      this.mediaStateService.mediaUploading$.pipe(
        tap((files) => {
          const hasUploadingFiles = files.some((file) => file.fileStatus === 'Uploading');
          if (!hasUploadingFiles) {
            this.dispatchMediaUploading(false);
          }
        })
      ),
    ])
      .pipe(
        map(([_, uploadedFile, filesUploading]) => ({ uploadedFile, filesUploading })),
        distinctUntilKeyChanged('filesUploading'),
        takeUntil(this.mediaNotifier)
      )
      .subscribe({
        next: (data) => this.handleUploadStatus(data, t0),
        error: (err) => console.error('Error in monitorMediaUpload', err),
      });
  }

  private createPollingStream(): Observable<number> {
    return interval(2000).pipe(tap(() => this.mediaStateService.getDeltaMedia()));
  }

  private createUploadedFileStream(specificFileMediaName: string): Observable<any> {
    return combineLatest([this.getSpecificFileStream(specificFileMediaName), this.getOemAssociateId()]);
  }

  private getSpecificFileStream(specificFileMediaName: string): Observable<any> {
    return this.mediaStateService.getSpecificFileMedia(specificFileMediaName).pipe(
      filter((files) => files && files.length === 1),
      map((files) => files[0]),
      distinctUntilKeyChanged('fileName'),
      tap((file) => {
        this.mediaStateService.mediaUploaded({
          fileName: specificFileMediaName,
          fileType: `${file.mediaExtension.substring(1)}`,
          fileStatus: 'Uploaded',
        });
      })
    );
  }

  private getOemAssociateId(): Observable<string | null> {
    return this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      filter((employee) => employee !== null),
      switchMap((employee) =>
        this.apiService
          .get(`v/1/system/oem-rooftop-links/${employee.rooftopId}`)
          .pipe(map((oemRooftopLink) => oemRooftopLink?.associateId ?? null))
      ),
      take(1)
    );
  }

  private handleUploadStatus(data: { uploadedFile: any; filesUploading: any[] }, t0: number): void {
    const hasUploadingFiles = data.filesUploading.map((file) => file.fileStatus).includes('Uploading');

    if (hasUploadingFiles) {
      this.showUploadingSnackbar();
    } else {
      this.showCompletedSnackbar();
      this.scheduleCleanup();
      this.handleMediaUploadCompletion(t0);
    }
  }

  private showUploadingSnackbar(): void {
    this.snackbarService.showCustomSnackbar(MediaUploadSnackbarComponent, {
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['media-snackbar'],
    });
  }

  private showCompletedSnackbar(): void {
    this.snackbarService.showCustomSnackbar(MediaUploadSnackbarComponent, {
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['media-snackbar'],
      duration: 3000,
    });
  }

  private scheduleCleanup(): void {
    setTimeout(() => {
      this.mediaStateService.clearUploadingMediaState();
    }, 4000);
  }

  private handleMediaUploadCompletion(t0: number) {
    const t1 = performance.now();
    datadogRum.addAction('mediaUploadSpeed', {
      time: `${Math.round(((t1 - t0) / 1000) * 100) / 100} seconds`,
    });
    this.mediaNotifier.next(true);
    this.mediaUploadTimer$.unsubscribe();
  }

  private getFileTypeForAnalytics(fileType: string): string | null {
    if (fileType.includes('image')) return 'image';
    if (fileType.includes('video')) return 'video';
    return null;
  }

  private simpleFileUpload(
    requestheaders: HttpHeaders,
    queryParams: any,
    fileName: string,
    fileType: string,
    t0: number,
    file: File,
    params: any
  ): Observable<any> {
    return this.http
      .get(`${environment.mediaPresignUrl}/${encodeURIComponent(fileName)}/${encodeURIComponent(fileType)}`, {
        headers: requestheaders,
        params: queryParams,
      })
      .pipe(
        switchMap((data: any) => {
          return this.uploadBlobWithRetry(data.uploadURL, file, fileType).pipe(
            tap(() => {
              this.mediaStateService.mediaUploaded(this.buildMediaUploadedPayload(fileName, fileType));
            })
          );
        }),
        tap(() => this.finalizeMediaUpload(fileName, fileType, t0, params, queryParams.duration))
      );
  }

  async analyzeCamera(stream: MediaStream): Promise<CameraAnalysis> {
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) throw new Error('No video track available');

    const settings = videoTrack.getSettings();
    const capabilities = videoTrack.getCapabilities();

    return {
      resolution: {
        width: settings.width || 1280,
        height: settings.height || 720,
      },
      pixels: (settings.width || 1280) * (settings.height || 720),
      frameRate: settings.frameRate || 30,
      aspectRatio: settings.aspectRatio || 16 / 9,
      facingMode: settings.facingMode || 'user',
      deviceCapabilities: capabilities,
    };
  }

  calculateOptimalBitrate(analysis: CameraAnalysis, quality: 'conservative' | 'balanced' | 'aggressive'): number {
    const { pixels, frameRate } = analysis;

    // Bits per pixel per frame based on quality level
    const bitsPerPixelPerFrame = {
      conservative: 0.05, // Lower quality, more reliable
      balanced: 0.08, // Good balance
      aggressive: 0.12, // Higher quality, may stress device
    };

    const baseBitrate = pixels * frameRate * bitsPerPixelPerFrame[quality];

    // Apply resolution-specific adjustments
    let adjustedBitrate = baseBitrate;

    if (pixels <= 640 * 480) {
      // 480p
      adjustedBitrate = Math.min(baseBitrate, 1000000); // Cap at 1Mbps
    } else if (pixels <= 1280 * 720) {
      // 720p
      adjustedBitrate = Math.min(baseBitrate, 3000000); // Cap at 3Mbps
    } else if (pixels <= 1920 * 1080) {
      // 1080p
      adjustedBitrate = Math.min(baseBitrate, 5000000); // Cap at 5Mbps
    } else {
      // 4K+
      adjustedBitrate = Math.min(baseBitrate, 8000000); // Cap at 8Mbps
    }

    return Math.round(adjustedBitrate);
  }

  determineTimeslice(analysis: CameraAnalysis, quality: 'conservative' | 'balanced' | 'aggressive'): number {
    const { pixels } = analysis;

    // More pixels = longer timeslice to reduce memory pressure
    if (pixels >= 1920 * 1080) {
      // 1080p+
      return quality === 'conservative' ? 5000 : 3000;
    } else if (pixels >= 1280 * 720) {
      // 720p
      return quality === 'conservative' ? 3000 : 2000;
    } else {
      // 480p
      return quality === 'conservative' ? 2000 : 1000;
    }
  }

  selectCodec(): string[] {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

    if (isIOS && isSafari) {
      return [
        'video/mp4; codecs="avc1.4d401f"', // Main profile for iPad
        'video/mp4; codecs="avc1.42E01E"',
        'video/mp4',
      ];
    }

    const isDesktop =
      (/Windows NT/.test(userAgent) || /Intel Mac OS X/.test(userAgent)) && !/Mobi|Android|iPhone|iPad/.test(userAgent);

    if (isDesktop) {
      return ['video/webm; codecs="vp9,opus"', 'video/mp4; codecs="avc1.4d401f"', 'video/mp4'];
    }

    return ['video/mp4; codecs="avc1.42E01E"', 'video/mp4'];
  }

  async generateOptimalConfig(
    stream: MediaStream,
    quality: 'conservative' | 'balanced' | 'aggressive' = 'balanced'
  ): Promise<MediaRecorderConfig> {
    const analysis = await this.analyzeCamera(stream);
    const deviceInfo = this.getDeviceInfo();

    this.debugLog(`=== DEVICE INFO DEBUG ===`);
    this.debugLog(`User Agent: ${navigator.userAgent}`);
    this.debugLog(`Device Info: ${JSON.stringify(deviceInfo)}`);
    this.debugLog(`isAndroid check: ${/Android/.test(navigator.userAgent)}`);

    this.debugLog('=== DYNAMIC CONFIGURATION ===');
    this.debugLog(`Resolution: ${analysis.resolution.width}x${analysis.resolution.height}`);
    this.debugLog(`Pixels: ${analysis.pixels.toLocaleString()}`);
    this.debugLog(`Frame Rate: ${analysis.frameRate}fps`);
    this.debugLog(`Quality Level: ${quality}`);

    // Calculate optimal bitrate
    const videoBitrate = this.calculateOptimalBitrate(analysis, quality);

    // Determine timeslice
    const timeslice = this.determineTimeslice(analysis, quality);

    // Select codec
    const supportedCodecs = this.selectCodec();

    this.debugLog('=== CODEC SUPPORT TEST ===');
    supportedCodecs.forEach((codec, index) => {
      const isSupported = MediaRecorder.isTypeSupported(codec);
      this.debugLog(`${index + 1}. ${codec}: ${isSupported ? 'SUPPORTED' : 'NOT SUPPORTED'}`);
    });

    const mimeType = supportedCodecs.find((codec) => MediaRecorder.isTypeSupported(codec)) || '';

    this.debugLog(`Optimal bitrate: ${(videoBitrate / 1000).toFixed(0)}kbps`);
    this.debugLog(`Timeslice: ${timeslice}ms`);
    this.debugLog(`Selected codec: ${mimeType}`);

    return {
      mimeType,
      videoBitsPerSecond: videoBitrate,
      audioBitsPerSecond: 128000, // Standard audio bitrate
      timeslice,
      quality,
    };
  }

  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    return {
      isIOS: /iPad|iPhone|iPod/.test(userAgent),
      isSafari: /Safari/.test(userAgent) && !/Chrome/.test(userAgent),
      isIPad: /iPad/.test(userAgent),
      isIPhone: /iPhone/.test(userAgent),
    };
  }

  // Quality presets for different use cases
  getQualityPresets(): Record<string, 'conservative' | 'balanced' | 'aggressive'> {
    return {
      'long-recording': 'conservative', // 10+ minutes
      standard: 'balanced', // 2-10 minutes
      'high-quality': 'aggressive', // < 2 minutes, quality priority
      'live-streaming': 'conservative', // Real-time constraints
      archive: 'aggressive', // Maximum quality for storage
    };
  }

  debugLog(message: string, data?: any) {
    if (environment.mediaDebugLogging) {
      const timestamp = new Date().toLocaleTimeString();
      const logEntry = `[${timestamp}] ${message}`;

      // Add to display logs (limited)
      this.debugLogs.unshift(logEntry);
      if (this.debugLogs.length > this.maxDebugLogs) {
        this.debugLogs = this.debugLogs.slice(0, this.maxDebugLogs);
      }

      // Add to full log file (unlimited)
      this.logFile.unshift(logEntry);

      console.log(logEntry, data);
    }
  }

  criticalLog(message: string, data?: any) {
    if (environment.mediaDebugLogging) {
      this.debugLog(message, data);
    }
  }

  // This would ideally be in the service lane facade, but keeping here due to circular dependency issues
  dispatchMediaUploading(mediaUploading: boolean) {
    this.store.dispatch({
      type: '[ServiceLane] Set Media Uploading In State',
      payload: mediaUploading,
    });
  }

  /**
   * Builds the payload for a media uploaded state update.
   * Strips the file extension from the fileName and extracts the subtype
   * from a MIME type string (e.g. 'video/mp4' -> 'mp4').
   *
   * @param fileName - The full file name including extension (e.g. 'video.mp4')
   * @param fileType - The MIME type of the file (e.g. 'video/mp4' or 'mp4')
   * @returns Payload object ready to pass to mediaStateService.mediaUploaded()
   */
  private buildMediaUploadedPayload(
    fileName: string,
    fileType: string
  ): { fileName: string; fileType: string; fileStatus: string } {
    const name = fileName.split('.')[0] || '';
    const fileTypeParts = fileType.split('/');
    const type = fileTypeParts.length > 1 ? fileTypeParts[1] : fileTypeParts[0] || '';

    return { fileName: name, fileType: type, fileStatus: 'Uploaded' };
  }

  /**
   * Executes a single PUT to a presigned S3 URL via XHR and emits the completed
   * XMLHttpRequest on a 2xx response. Errors on network failure, abort, or non-2xx.
   *
   * XHR is used instead of HttpClient because only XHR exposes upload progress events,
   * which are required for stall detection. If no bytes advance within STALL_TIMEOUT_MS,
   * the request is aborted so callers can retry. `label` is included in log/error messages
   * to distinguish simple uploads from multipart chunks.
   */
  private xhrPut(presignedUrl: string, blob: Blob, contentType: string, label: string): Observable<XMLHttpRequest> {
    return new Observable<XMLHttpRequest>((observer) => {
      const xhr = new XMLHttpRequest();
      let completed = false; // track whether the request finished naturally
      let lastLoaded = 0;
      let lastProgressTime = Date.now();

      const stallCheck = setInterval(() => {
        if (Date.now() - lastProgressTime > this.STALL_TIMEOUT_MS) {
          if (environment.mediaDebugLogging) {
            console.warn(
              `[MediaUpload] Stall detected on ${label} after ${
                Date.now() - lastProgressTime
              }ms with ${lastLoaded} bytes loaded`
            );
          }
          clearInterval(stallCheck);
          xhr.abort();
        }
      }, this.STALL_CHECK_INTERVAL_MS);

      xhr.upload.onprogress = (event) => {
        if (event.loaded > lastLoaded) {
          lastLoaded = event.loaded;
          lastProgressTime = Date.now();
        }
      };

      xhr.onload = () => {
        completed = true;
        clearInterval(stallCheck);
        if (xhr.status >= 200 && xhr.status < 300) {
          observer.next(xhr);
          observer.complete();
        } else {
          observer.error(new Error(`[MediaUpload] S3 HTTP ${xhr.status} on ${label}`));
        }
      };

      xhr.onerror = () => {
        completed = true;
        clearInterval(stallCheck);
        observer.error(new Error(`[MediaUpload] Network error on ${label}`));
      };

      xhr.onabort = () => {
        completed = true;
        clearInterval(stallCheck);
        observer.error(new Error(`[MediaUpload] Aborted (stall) on ${label}`));
      };

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(blob);

      return () => {
        clearInterval(stallCheck);
        // Only abort if the request is still in-flight — teardown also runs on
        // normal completion, and aborting a finished request triggers onabort spuriously
        if (!completed) {
          // Remove handlers before aborting to prevent onabort firing into a
          // already-completing observer
          xhr.onabort = null;
          xhr.abort();
        }
      };
    });
  }

  /**
   * Uploads a blob to a presigned S3 URL with stall detection and exponential backoff.
   * Handles mobile tower handoffs and brief signal drops without user intervention.
   */
  private uploadBlobWithRetry(presignedUrl: string, blob: Blob, contentType: string, attempt = 0): Observable<void> {
    return this.xhrPut(presignedUrl, blob, contentType, 'upload').pipe(
      map((): void => void 0),
      catchError((err) => {
        if (attempt < this.MAX_RETRY_ATTEMPTS && this.isRetriableError(err)) {
          const delay = this.BASE_BACKOFF_MS * Math.pow(2, attempt);
          console.log(
            `[MediaUpload] Retrying upload after ${delay}ms (attempt ${attempt + 1}/${this.MAX_RETRY_ATTEMPTS})`
          );
          return timer(delay).pipe(
            switchMap(() => this.uploadBlobWithRetry(presignedUrl, blob, contentType, attempt + 1))
          );
        }
        // Non-retriable or retries exhausted — surface immediately
        if (!this.isRetriableError(err)) {
          console.error(`[MediaUpload] Non-retriable error, failing fast:`, err.message);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Uploads a single multipart chunk and returns its PartNumber + ETag.
   * S3 requires the ETag from each part response to finalize the multipart upload.
   * Only this chunk is retried on failure — successfully uploaded chunks are unaffected.
   */
  private uploadChunkWithRetry(
    presignedUrl: string,
    chunk: Blob,
    contentType: string,
    partNumber: number,
    attempt = 0
  ): Observable<{ PartNumber: number; ETag: string }> {
    return this.xhrPut(presignedUrl, chunk, contentType, `part ${partNumber}`).pipe(
      map((xhr) => {
        const etag = xhr.getResponseHeader('ETag')?.replace(/"/g, '');
        if (!etag) {
          throw new Error(`[MediaUpload] ETag missing for part ${partNumber}`);
        }
        return { PartNumber: partNumber, ETag: etag };
      }),
      catchError((err) => {
        if (attempt < this.MAX_RETRY_ATTEMPTS && this.isRetriableError(err)) {
          const delay = this.BASE_BACKOFF_MS * Math.pow(2, attempt);

          return timer(delay).pipe(
            switchMap(() => this.uploadChunkWithRetry(presignedUrl, chunk, contentType, partNumber, attempt + 1))
          );
        }
        // Non-retriable or retries exhausted — surface immediately
        if (!this.isRetriableError(err)) {
          console.error(`[MediaUpload] Non-retriable error, failing fast:`, err.message);
        }
        return throwError(() => err);
      })
    );
  }

  // Errors that indicate a definitive failure — retrying won't help and adds unnecessary delay.
  // 403 covers expired/mismatched presigned URLs, 400 covers malformed requests.
  private isRetriableError(err: Error): boolean {
    const message = err.message ?? '';
    if (message.includes('HTTP 400') || message.includes('HTTP 403')) return false;
    // 4xx generally = client error, not transient. However, 408 (timeout) and 429 (rate limit)
    // are typically transient and may succeed on retry. 5xx and network/stall errors are retriable.
    const match = message.match(/HTTP (\d{3})/);
    if (match) {
      const status = parseInt(match[1]);
      if (status === 408 || status === 429) {
        return true;
      }
      // Only retry server-side failures; other 4xx are treated as non-retriable.
      return status >= 500;
    }
    // Network errors, aborts, stalls have no HTTP status in the message — retriable
    return true;
  }
}
